import React, { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { format } from "date-fns";
import theme from "../../Styles/theme.scss";
import CareActChart from "./CareActChart";
import { processClipboardData } from "./processClipboardData";
import LilliButton from "Components/DesignSystemComponents/LilliButton/LilliButton";
import classNames from "classnames";
import { CircleCheckIcon } from "Styles/Icons/DesignSystem/CircleCheckIcon";
import { useDownloadReport } from "Services/Hooks";
import { ReportResponseBody } from "@intelligentlilli/api-layer";
import NewCopyIcon from "Styles/Icons/DesignSystem/NewCopyIcon";
import { useAmplitudeTracking } from "Services/Hooks/useAmplitudeTracking";

const LineBreak = () => {
  return <div className={styles.line_break}></div>;
};

const PageNumber = ({ number, total }: { number: number; total: number }) => (
  <div className={styles.pageNumber} key={number}>
    Page {number} of {total}
  </div>
);

const Question = ({
  question,
  isPdf,
  firstName,
  startDates,
  formattedDateRange,
  onTrack,
}: {
  question: any;
  isPdf?: boolean;
  firstName: string;
  startDates: any;
  formattedDateRange: any;
  onTrack: (eventProperties: Record<string, string>) => void;
}) => {
  const [copiedClicked, setCopiedClicked] = useState<boolean>(false);

  useEffect(() => {
    if (copiedClicked) {
      setTimeout(() => {
        setCopiedClicked(false);
      }, 2000);
    }
  }, [copiedClicked]);

  let sentences = [];
  question.sentences.data && sentences.push(question.sentences.data);
  question.sentences.lowerbound_risk &&
    sentences.push(question.sentences.lowerbound_risk);
  question.sentences.upperbound_risk &&
    sentences.push(question.sentences.upperbound_risk);
  question.sentences.trend && sentences.push(question.sentences.trend);
  const sentencesWithNames = sentences.map((sentence) =>
    sentence?.replace("{name}", firstName)
  );
  const questionText = question.question_text.replace(
    "<placeholder_name>",
    firstName
  );

  return (
    <div className={styles.section}>
      {question.sectionTitle && (
        <h2 data-private>
          {question.sectionTitle.replace("<placeholder_name>", firstName)}
        </h2>
      )}
      <div className={styles.section_question}>
        <div className={styles.section_question_text}>
          <h3 data-private>
            {question.question_id.slice(1)}.&nbsp;
            {questionText}
          </h3>
          <ul data-private>
            {sentencesWithNames.map((sentence, index) => (
              <li key={index}>{sentence}</li>
            ))}
          </ul>
          <LilliButton
            variant="primary"
            className={classNames({
              [styles.hide]: isPdf,
            })}
            leftIcon={
              copiedClicked ? (
                <CircleCheckIcon
                  colour={isPdf ? theme.white : theme.neutral7}
                />
              ) : (
                <NewCopyIcon colour={isPdf ? theme.white : theme.neutral7} />
              )
            }
            onClick={() => {
              onTrack({ action: "text_copied" });
              setCopiedClicked(true);
              navigator.clipboard.writeText(
                `• ${sentencesWithNames.join("\n• ")}`
              );
            }}
          >
            {copiedClicked ? "Copied!" : "Copy text"}
          </LilliButton>
        </div>
        <CareActChart
          isPdf={isPdf}
          chartData={question.chart}
          startDates={startDates}
          questionText={questionText}
          formattedDateRange={formattedDateRange}
          onTrack={onTrack}
        />
      </div>
    </div>
  );
};

interface CareActProps {
  reportData: ReportResponseBody;
  isPdf?: boolean;
  language?: "en" | "fr";
}

const CareAct = ({ reportData, isPdf, language = "en" }: CareActProps) => {
  const [processedReportData, setProcessedReportData] = useState(undefined);
  const [copiedClicked, setCopiedClicked] = useState(false);

  const track = useAmplitudeTracking();

  const trackReportsV2 = (eventProperties: Record<string, string>) => {
    track("Reports Event", {
      ...eventProperties,
      location: "report_preview_v2",
    });
  };

  const { getButtonText, getButtonIcon, error, downloadReport } =
    useDownloadReport({
      report: reportData,
    });

  // useEffect to reset the copiedClicked state after 2 seconds
  useEffect(() => {
    if (copiedClicked) {
      setTimeout(() => {
        setCopiedClicked(false);
      }, 2000);
    }
  }, [copiedClicked]);

  const getUnknownInCorrectLanguage = language === "fr" ? "Inconnu" : "Unknown";

  const firstName =
    reportData?.entity_properties?.forenames ||
    reportData?.entity_properties?.fullName?.split(" ")[0] ||
    getUnknownInCorrectLanguage;
  const surname = reportData?.entity_properties?.surname;
  const fullName =
    reportData?.entity_properties?.fullName || `${firstName} ${surname}`;

  // Function to split report data into different pages
  const splitReportData = (reportData: ReportResponseBody) => {
    // Get the sections from the report data
    const questions = reportData.sections
      .flatMap((section) =>
        section.questions.map((question, index) =>
          index === 0
            ? { ...question, sectionTitle: section.section_title }
            : question
        )
      )
      .map((question, index) => ({
        ...question,
        question_id: `Q${index + 1}`,
      }));
    // Split off the first two questions to be on the first page
    const firstPageQuestions = questions.slice(0, 2);
    questions.splice(0, 2);

    // Split the questions into groups of 3
    const pages = questions.reduce((resultArray, item, index) => {
      const chunkIndex = Math.floor(index / 3);
      if (!resultArray[chunkIndex]) {
        resultArray[chunkIndex] = [];
      }
      resultArray[chunkIndex].push(item);
      return resultArray;
    }, []);

    return { pages: pages, firstPageQuestions: firstPageQuestions };
  };

  if (!processedReportData && reportData) {
    setProcessedReportData(splitReportData(reportData));
  }

  const totalPages = (processedReportData?.pages?.length || 0) + 1;
  if (!reportData) {
    return <div>test</div>;
  }

  const formattedDateRange = `${format(
    new Date(reportData?.start_at),
    "dd MMMM yyyy"
  )} - ${format(new Date(reportData?.end_at), "dd MMMM yyyy")}`;

  const userDetailsKeys = [
    { key: "fullName", label: { en: "Full Name:", fr: "Nom et prénom:" } },
    { key: "careId", label: { en: "Care ID:", fr: "N° d'identification:" } },
    {
      key: "dateOfBirth",
      label: { en: "Date of Birth:", fr: "Date de naissance:" },
      value: reportData?.entity_properties?.dateOfBirth
        ? format(
            new Date(reportData.entity_properties.dateOfBirth),
            "dd/MM/yyyy"
          )
        : getUnknownInCorrectLanguage,
    },
    {
      key: "age",
      label: { en: "Age:", fr: "Âge:" },
      value: reportData?.entity_properties?.dateOfBirth
        ? `${new Date().getFullYear() - new Date(reportData.entity_properties.dateOfBirth).getFullYear()}`
        : getUnknownInCorrectLanguage,
    },
    { key: "phoneNumber", label: { en: "Tel. No:", fr: "N° de tél:" } },
  ];

  const userDetailsToRender = userDetailsKeys.flatMap((item) => [
    item.label?.[language] || item.label.en, // Label based on language
    item.value ||
      reportData?.entity_properties?.[item.key] ||
      getUnknownInCorrectLanguage, // Value
  ]);

  const reportDetails = [
    {
      key: "reportCreated",
      label: { en: "Report created:", fr: "Rapport créé:" },
      value: format(new Date(reportData?.created_at), "dd/MM/yy"),
    },
    {
      key: "createdBy",
      label: { en: "Created by:", fr: "Créé par:" },
      value:
        reportData?.entity_properties?.createdBy || getUnknownInCorrectLanguage,
    },
  ];

  const reportDetailsToRender = reportDetails.flatMap((item) => [
    item.label?.[language] || item.label.en, // Label based on language
    item.value,
  ]);

  return (
    <>
      {/* This is the first page of the pdf document - it is always present */}
      <div className={classNames(styles.pdf_first, styles.flex_start)}>
        <div
          className={classNames({
            [styles.hide]: isPdf,
            [styles.copy]: !isPdf,
          })}
        >
          <LilliButton
            variant={error ? "warning" : "primary"}
            onClick={() => {
              trackReportsV2({ action: "download_report" });
              downloadReport();
            }}
            leftIcon={getButtonIcon()}
            className={styles.fixed_width_button}
          >
            {getButtonText()}
          </LilliButton>
          <LilliButton
            variant="secondary"
            className={styles.fixed_width_button}
            leftIcon={
              copiedClicked ? (
                <CircleCheckIcon
                  colour={isPdf ? theme.white : theme.neutral7}
                />
              ) : (
                <NewCopyIcon colour={isPdf ? theme.white : theme.neutral7} />
              )
            }
            onClick={() => {
              trackReportsV2({ action: "all_text_copied" });
              setCopiedClicked(true);
              navigator.clipboard.writeText(
                processClipboardData({
                  reportData,
                  userDetailsToRender,
                  reportDetailsToRender,
                  firstName,
                })
              );
            }}
          >
            {copiedClicked ? "Copied!" : "Copy all text"}
          </LilliButton>
        </div>
        <img src="/logo/lilli-128-teal.png" alt="Lilli" width="60" />
        <h1 data-private>{`${fullName} - ${reportData?.report_title}`}</h1>
        <p className={styles.dateRange}>
          {language === "fr" ? "Rapport pour" : "Report for"}:{" "}
          {formattedDateRange}
        </p>
        <div className={styles.details}>
          <div className={styles.details_section}>
            <h3>
              {language === "fr"
                ? "Détails de l'utilisateur Lilli"
                : "Resident details"}
            </h3>
            <div className={styles.details_section_grid} data-private>
              {userDetailsToRender.map((item, index) => (
                <div
                  key={index}
                  className={index % 2 ? styles.gridItem : styles.gridItem_bold}
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
          <div className={styles.details_section}>
            <h3>
              {language === "fr" ? "Détails du rapport" : "Report details"}
            </h3>
            <div className={styles.details_section_grid} data-private>
              {reportDetailsToRender.map((item, index) => (
                <div
                  key={index}
                  className={index % 2 ? styles.gridItem : styles.gridItem_bold}
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
        {processedReportData?.firstPageQuestions?.map((question, index) => (
          <>
            {index !== 0 && <LineBreak />}
            <Question
              question={question}
              isPdf={isPdf}
              key={index}
              firstName={firstName}
              startDates={reportData?.start_dates}
              formattedDateRange={formattedDateRange}
              onTrack={trackReportsV2}
            />
          </>
        ))}
        <PageNumber number={1} total={totalPages} />
      </div>

      {/* These are the subsequent pages of the pdf document */}
      {processedReportData?.pages?.map((page, index: number) => (
        <div
          className={`${styles.pdf} ${
            page.length === 3 ? styles.space_evenly : styles.flex_start
          }`}
          key={index}
        >
          {page.map((question, index: number) => (
            <>
              {index !== 0 && <LineBreak />}
              <Question
                question={question}
                isPdf={isPdf}
                key={index}
                firstName={firstName}
                startDates={reportData?.start_dates}
                onTrack={trackReportsV2}
              />
            </>
          ))}
          <PageNumber number={index + 2} total={totalPages} />
        </div>
      ))}
    </>
  );
};

export default CareAct;
